/**
 * Registratur fuer „Sicherungen vor der Bauphase".
 *
 * Der Flush in useBauphaseGate (`flushAllNow`) erreicht nur die Warteschlange
 * und das aktuell offene Projekt der Innendienst-Ansicht. Alles, was
 * anderswo NUR im React-State liegt — heute die laufende Freelancer-Begehung
 * (Maengel, Pruefpunkte, Speichern ueber `freelancer_save_inspection`) —,
 * wuerde beim Umschalten auf die Wartungsseite mit dem Baum verschwinden.
 *
 * Deshalb: wer ungesicherte Eingaben haelt, meldet hier eine Sicherung an;
 * das Gate ruft VOR seinem eigenen Flush `runBauphaseFlushers()` und wartet
 * darauf. Bewusst ein Modul ohne React und ohne Supabase — nur eine Map.
 *
 * Fehler einer Sicherung werden geloggt und geschluckt: die Sperre darf an
 * keiner einzelnen Sicherung haengen bleiben, der Server nimmt ohnehin bald
 * nichts mehr an. Der Verlust bleibt dann auf diese eine Sicherung begrenzt.
 */

export type BauphaseFlusher = () => Promise<void>;

const flushers = new Map<string, BauphaseFlusher>();

/**
 * Sicherung anmelden. Dieselbe id ersetzt eine fruehere Anmeldung (ein
 * Effekt, der neu laeuft, meldet nicht doppelt). Liefert das Abmelden —
 * das nur die EIGENE Anmeldung entfernt, nicht eine spaetere unter derselben id.
 */
export const registerBauphaseFlusher = (id: string, fn: BauphaseFlusher): (() => void) => {
    flushers.set(id, fn);
    return () => {
        if (flushers.get(id) === fn) flushers.delete(id);
    };
};

/** Gibt es angemeldete Sicherungen? (Anzeige/Diagnose, keine Steuerung.) */
export const hasPendingBauphaseFlushers = (): boolean => flushers.size > 0;

/**
 * Alle angemeldeten Sicherungen ausfuehren und auf alle warten. Laeuft
 * ueber `Promise.allSettled`: eine gescheiterte Sicherung stoppt weder die
 * anderen noch den Aufrufer. Die Liste wird VOR dem Start eingefroren —
 * eine Sicherung, die sich waehrenddessen abmeldet, laeuft trotzdem zu Ende.
 */
export const runBauphaseFlushers = async (): Promise<void> => {
    const entries = Array.from(flushers.entries());
    if (entries.length === 0) return;
    const results = await Promise.allSettled(entries.map(([, fn]) => fn()));
    results.forEach((r, i) => {
        if (r.status === 'rejected') {
            console.warn(`[bauphase] Sicherung "${entries[i][0]}" fehlgeschlagen:`, r.reason);
        }
    });
};

/** Nur fuer Tests: Registratur leeren. */
export const __resetBauphaseFlushersForTest = (): void => {
    flushers.clear();
};
